/*
 * WhatsApp number status counts (User Data / CRM Lead Data), shared by:
 *   CXOPanel/whatsappDataAnalysis.js       (by source — getWhatsappData, exam key "examTypeId")
 *   CXOPanel/whatsappDataStateAnalysis.js  (by state  — getWhatsappStateData, exam key "examId")
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { MessageCircle } from "lucide-react";
import { getExamListForWhatsapp } from "../../Action/courseAction";
import { CustomAdminTable, CustomBadge, CustomPageLayout } from "../../Component/customComponents";
import { StatusDropdown } from "./transactionCommon";
import { exportRowsToExcel } from "./countListCommon";

const DATA_TYPES = [
  { _id: 1, name: "User Data" },
  { _id: 2, name: "CRM Lead Data" },
];

export const WHATSAPP_COUNT_COLUMNS = [
  { key: "totalCount", header: "Total Count" },
  { key: "isWhatsAppCount", header: "Is WhatsApp" },
  { key: "isNotWhatsAppCount", header: "Is Not WhatsApp" },
  { key: "notUpdatedCount", header: "Not Updated" },
  { key: "OptedInCount", header: "Opted In" },
  { key: "OptedOutCount", header: "Opted Out" },
  { key: "BlockedCount", header: "Blocked" },
  { key: "UndeliverableCount", header: "Undeliverable" },
];

const n = (v) => Number(v) || 0;
const fmt = (v) => n(v).toLocaleString("en-IN");

/**
 * Props: pageTitle, tableId, fetchAction, examKey ("examTypeId" | "examId"),
 *        nameKey / nameHeader (first column: "typeName"/"Source" or "stateName"/"State"), exportName
 */
export const WhatsappDataPage = ({ pageTitle, tableId, fetchAction, examKey, nameKey, nameHeader, exportName }) => {
  const dispatch = useDispatch();
  const [examList, setExamList] = useState([]);
  const [filters, setFilters] = useState({ exam: "", examStream: "" });
  const [dataType, setDataType] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /** same request as before: { dataType, [examKey]?, baseCourseId? } */
  const fetchRows = (f = filters, type = dataType) => {
    setLoading(true);
    dispatch(
      fetchAction(
        {
          dataType: type || 1,
          ...(f.exam?._id && { [examKey]: f.exam._id }),
          ...(f.examStream?._id && { baseCourseId: f.examStream._id }),
        },
        (res) => {
          setRows(Array.isArray(res?.data) ? res.data : []);
          setLoading(false);
        },
        () => {
          setRows([]);
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    dispatch(getExamListForWhatsapp((res) => setExamList(res?.data || [])));
    fetchRows(filters, 1);
  }, []);

  const filterFields = useMemo(
    () => [
      { key: "exam", label: "Exam", type: "dropdown", options: examList, clears: ["examStream"] },
      {
        key: "examStream",
        label: "Exam Stream",
        type: "dropdown",
        options: (v) => v.exam?.baseCourseId || [], // streams come with the exam
        disabled: (v) => !v.exam,
        hint: (v) => (!v.exam ? "Select an exam first" : undefined),
      },
    ],
    [examList],
  );

  // the old table's first "Total" row
  const totals = useMemo(
    () => Object.fromEntries(WHATSAPP_COUNT_COLUMNS.map((c) => [c.key, rows.reduce((t, r) => t + n(r?.[c.key]), 0)])),
    [rows],
  );
  const tableRows = useMemo(() => (rows.length ? [{ _total: true, [nameKey]: "Total", ...totals }, ...rows] : []), [rows, totals, nameKey]);

  const columns = [
    { key: nameKey, header: nameHeader, minWidth: 170, render: (v) => <strong>{v || "—"}</strong> },
    ...WHATSAPP_COUNT_COLUMNS.map((c) => ({ key: c.key, header: c.header, align: "right", minWidth: 105, render: fmt })),
  ];

  const onExport = () =>
    exportRowsToExcel(
      exportName,
      exportName,
      tableRows.map((r) => ({ [nameHeader.toUpperCase()]: r[nameKey] || "-", ...Object.fromEntries(WHATSAPP_COUNT_COLUMNS.map((c) => [c.header.toUpperCase(), n(r[c.key])])) })),
    );

  const typeName = DATA_TYPES.find((t) => t._id === dataType)?.name;

  return (
    <CustomPageLayout title={pageTitle}>
      <CustomAdminTable
        enableSerialNumber
        serialHeader="S.No"
        id={tableId}
        title={
          <span>
            {nameHeader} wise ({rows.length})
            <span className="d-block small fw-normal text-muted">
              {typeName}
              {filters.exam ? ` · ${filters.exam.name}` : ""}
              {filters.examStream ? ` · ${filters.examStream.name}` : ""}
            </span>
          </span>
        }
        columns={columns}
        data={tableRows}
        loading={loading}
        rowKey={(r, i) => (r._total ? "total" : `${r?.[nameKey]}-${i}`)}
        rowClassName={(r) => (r._total ? "cat-rowGrandTotal" : "")}
        filters={
          <StatusDropdown
            options={DATA_TYPES}
            value={dataType}
            onChange={(t) => {
              setDataType(t);
              fetchRows(filters, t);
            }}
          />
        }
        toolbarActions={
          rows.length > 0 && (
            <CustomBadge tone="success" icon={<MessageCircle size={12} />}>
              On WhatsApp: {fmt(totals.isWhatsAppCount)} / {fmt(totals.totalCount)}
            </CustomBadge>
          )
        }
        exportButton={rows.length > 0}
        onExport={onExport}
        advancedFilters={filterFields}
        advancedFilterValues={filters}
        onApplyFilters={(values) => {
          setFilters(values);
          fetchRows(values, dataType);
        }}
        applyFiltersOnReset
        filterSubtitle="Narrow by exam and stream"
        emptyTitle="No records"
        emptyDescription="Nothing for this selection."
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
};
